import { store, store$ } from '@redux/store';
import { createSelector } from '@reduxjs/toolkit';
import { getPaging, routingSelectors, routingStateActions } from 'features/routing';
import { pageCountSelector, selectPage } from 'features/searchResultsFacets';
import { distinctUntilChanged, filter, map, skip, withLatestFrom } from 'rxjs';
import ResultPresentationType from 'layout/constants/resultPresentationType';
import { selectResultsPresentationType } from 'layout/redux/selectors';
import { getNextPageUrl, getPreviousPageUrl } from '../services/pageUrlBuilder';

const { selectCurrentUrl, urlComponentsSelector } = routingSelectors;
const pageLinkDataSelector = createSelector(
  selectCurrentUrl,
  urlComponentsSelector,
  pageCountSelector,
  selectPage,
  (currentUrl, urlComponents, pageCount, currentPage) => ({ currentUrl, urlComponents, pageCount, currentPage })
);

const presentationType$ = store$.pipe(map(selectResultsPresentationType), distinctUntilChanged());

/**
 * cleanUpPreviousAndNextPages: this function clean up the previous and next pages for SEO concerns.
 */
const cleanUpPreviousAndNextPages = () => {
  store.dispatch(routingStateActions.updatePreviousPageUrl(''));
  store.dispatch(routingStateActions.updateNextPageUrl(''));
};

const urlChange$ = store$.pipe(
  map(pageLinkDataSelector),
  distinctUntilChanged(),
  skip(1),
  withLatestFrom(presentationType$),
  filter(([, presentationType]) => presentationType !== ResultPresentationType.InfiniteScrolling.toString()),
  map(([urlInformation]) => urlInformation)
);

/**
 * updateNextPageUrl: Updates the next page url on the store.
 * @param {Object} urlComponents Object with the components used on the url.
 * @param {Number} currentPage Current page of the search.
 */
function updateNextPageUrl({ urlComponents, currentPage, pageCount }) {
  const { urlSubdirectory, urlLocation, urlQueryString } = urlComponents;
  const nextPageUrlPagination = getPaging(currentPage + 1);

  const nextPageUrlComponents = {
    urlSubdirectory,
    urlLocation,
    urlPaging: nextPageUrlPagination,
    urlQueryString,
  };
  const pagingInformation = {
    currentPage,
    lastPage: pageCount,
  };

  const nextPageUrl = getNextPageUrl(pagingInformation, nextPageUrlComponents);
  setTimeout(() => store.dispatch(routingStateActions.updateNextPageUrl(nextPageUrl)), 0);
}

urlChange$.subscribe(updateNextPageUrl);

/**
 * updatePreviousPageUrl: Updates the previous page url on the store.
 * @param {Object} urlComponents Object with the components used on the url.
 * @param {Number} currentPage Current page of the search.
 */
function updatePreviousPageUrl({ urlComponents, currentPage }) {
  const { urlSubdirectory, urlLocation, urlQueryString } = urlComponents;
  const previousPageUrlPagination = getPaging(currentPage - 1);

  const previousPageUrlComponents = {
    urlSubdirectory,
    urlLocation,
    urlPaging: previousPageUrlPagination,
    urlQueryString,
  };

  const previousPageUrl = getPreviousPageUrl(currentPage, previousPageUrlComponents);
  setTimeout(() => store.dispatch(routingStateActions.updatePreviousPageUrl(previousPageUrl)), 0);
}

urlChange$.subscribe(updatePreviousPageUrl);

/**
 * Subscribing to the store, if loading status is succeeded (ajax rendered results), and also
 * the current Search Results Page is showing results in an infinite scroll way, then we clean
 * up the previous and next pages in SEO since there is no pagination.
 */
store$
  .pipe(
    map(selectResultsPresentationType),
    distinctUntilChanged(),
    filter((presentationType) => presentationType === ResultPresentationType.InfiniteScrolling.toString())
  )
  .subscribe(cleanUpPreviousAndNextPages);
